import { useEffect, useState } from "react";
import { Crown, Lock, RotateCw } from "lucide-react";

// LockedFeatureCard — shown when a free user has exhausted their quota
// for a feature. Displays feature-specific copy and a live countdown to
// the reset time (`resetAt`). If the countdown expires while on-screen,
// the card auto-swaps in a "Try again" button so the user can proceed
// without a manual page refresh.
export function LockedFeatureCard({
  feature = "fitness",     // "fitness" | "generic"
  title,                   // string (optional; falls back to per-feature default)
  body,                    // string (optional; falls back to per-feature default)
  cta,                     // string (optional; falls back to per-feature default)
  resetAt,                 // ISO timestamp of reset
  onUpgrade,               // () => void
  onRetry,                 // () => void — called when the timer hits 0 and the user taps Try again
  testId = "locked-feature",
}) {
  const defaults = {
    fitness: {
      title: "Keep Your Progress Going",
      body:
        "You've completed your free workout. Your next session is ready when you are.\n\n" +
        "Your free Fitness access will reset in {countdown}, or upgrade to IX NOVA Premium for unlimited access to your personalized workout plan and uninterrupted progress.",
      cta: "Unlock Unlimited Fitness",
    },
    generic: {
      title: "You're On A Roll",
      body:
        "You've used your free allowance for now. Your next free use is ready in {countdown}, or upgrade to IX NOVA Premium for unlimited access.",
      cta: "Upgrade to Premium",
    },
  };
  const d = defaults[feature] || defaults.generic;
  const [countdown, setCountdown] = useState(() => humanizeReset(resetAt));

  useEffect(() => {
    if (!resetAt) return;
    const iv = setInterval(() => setCountdown(humanizeReset(resetAt)), 60_000);
    return () => clearInterval(iv);
  }, [resetAt]);

  const expired = resetAt && new Date(resetAt).getTime() <= Date.now();

  const rawBody = body || d.body;
  const filledBody = rawBody.replace(
    "{countdown}",
    expired ? "any moment" : (countdown || "2 days"),
  );

  return (
    <div className="fitness-locked" data-testid={testId}>
      <div className="premium-gate-icon" style={{ margin: "0 auto 14px" }}>
        <Lock size={22} />
      </div>
      <div className="premium-gate-title" data-testid={`${testId}-title`}>
        <span role="img" aria-hidden="true">🔒</span> {title || d.title}
      </div>
      <div
        className="premium-gate-body"
        data-testid={`${testId}-body`}
        style={{ whiteSpace: "pre-line" }}
      >
        {filledBody}
      </div>
      {expired && onRetry ? (
        <button
          type="button"
          className="btn btn-gold"
          onClick={onRetry}
          data-testid={`${testId}-retry`}
          style={{ width: "100%", justifyContent: "center", marginTop: 6 }}
        >
          <RotateCw size={14} /> Try again
        </button>
      ) : (
        <button
          type="button"
          className="btn btn-gold"
          onClick={onUpgrade}
          data-testid={`${testId}-cta`}
          style={{ width: "100%", justifyContent: "center", marginTop: 6 }}
        >
          <Crown size={14} /> {cta || d.cta}
        </button>
      )}
    </div>
  );
}

// humanizeReset — turn an ISO string into a friendly "X days" / "X hours"
// / "X minutes" label. Returns "" if resetAt is missing or in the past.
export function humanizeReset(resetAt) {
  if (!resetAt) return "";
  const ms = new Date(resetAt).getTime() - Date.now();
  if (!(ms > 0)) return "";
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `${Math.max(1, mins)} minute${mins === 1 ? "" : "s"}`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"}`;
  const days = Math.round(hrs / 24);
  return `${days} day${days === 1 ? "" : "s"}`;
}
